import clsx from 'clsx';
import Image from 'next/image';
import { HighlightedText } from '@/app/components/HighlightedText';
import type { GlobalSearchClientsItem } from '@/types/globalSearchApiTypes';
import { SearchItemTitle } from '../SearchItemTitle';
import { SearchItemWrapper } from '../SearchItemWrapper';

interface ClientsItemProps {
  item: GlobalSearchClientsItem;
}

export const ClientsItem = ({ item }: ClientsItemProps) => {
  const fullName = `${item.first_name} ${item.last_name}`;
  const initials = [item.first_name, item.last_name].map((name) => name[0]).join('');

  const avatar = item.profile_picture_url;

  return (
    <SearchItemWrapper href={item.redirect_url}>
      <div className="flex items-center gap-2 overflow-hidden">
        <div className="flex self-start">
          {avatar ? (
            <Image
              src={avatar}
              alt={fullName}
              width={32}
              height={32}
              className="rounded-full object-cover self-start min-w-[32px] min-h-[32px]"
            />
          ) : (
            <div className="flex justify-center items-center w-8 h-8 rounded-full bg-grey-100 border-2 border-black/[.12]">
              <span className="font-inter text-[12px] font-semibold text-neutral-700 uppercase">{initials}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 min-w-0 flex-1">
          <div className="flex items-center gap-[6px]">
            <SearchItemTitle>{fullName}</SearchItemTitle>
            <div
              className={clsx(
                'w-[6px] h-[6px] rounded-full',
                item.status === 'ACTIVE' ? 'bg-status-success' : 'bg-yellow-500'
              )}
            />
            {/* VIP and Admin badges are removed as is_vip and is_admin are no longer in the API response */}
            {item.is_vip && (
              <div className="flex justify-center items-center rounded-[4px] px-[12px] py-[2px] border border-accent-gold">
                <span className="font-inter text-[12px] leading-[140%] text-blue-300">VIP</span>
              </div>
            )}
            {/* {item.is_admin && (
              <div className="flex justify-center items-center bg-blue-100 rounded-[4px] px-[8px] py-[2px]">
                <span className="font-inter text-[12px] leading-[140%] text-blue-900">Admin</span>
              </div>
            )} */}
          </div>
          <div className="flex gap-1 min-w-0 max-md:flex-wrap">
            <p className="text-body-small text-grey-700 whitespace-nowrap max-md:whitespace-normal">
              {item.identity && (
                <span>
                  ID:{' '}
                  <span className="text-black">
                    <HighlightedText>{item.identity}</HighlightedText>
                  </span>
                  ;{' '}
                </span>
              )}
              {item.email && <span>{<HighlightedText>{item.email}</HighlightedText>}; </span>}
              {item.phone && <span>{<HighlightedText>{item.phone}</HighlightedText>}; </span>}
            </p>
            {item.agent_name && (
              <p className="text-body-small text-grey-700 whitespace-nowrap max-md:whitespace-normal">
                <span>agent: </span>
                <span className="text-black">
                  <HighlightedText>{item.agent_name}</HighlightedText>;
                </span>
              </p>
            )}
            {(item.travelers?.length ?? 0) > 0 && (
              <div className="flex gap-1 min-w-0 flex-1">
                <span className="text-body-small text-grey-700  flex-shrink-0">travelers:</span>
                <span className="text-body-small text-black truncate">
                  <HighlightedText>{item.travelers?.join(', ')}</HighlightedText>;
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </SearchItemWrapper>
  );
};
